import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { FaShoppingBag } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import logo from "../../images/logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";

// Styled Components
const Header = tw.header`
  flex justify-between items-center max-w-screen-xl mx-auto
  mt-[40px] mb-[80px] lg:mb-[40px] px-4 sm:px-6 lg:px-8
`;

const NavLinks = tw.div`flex items-center gap-2`;

// Changed NavLink to use 'Link' from react-router-dom for internal navigation
const NavLink = styled(Link)`
  ${tw`
    text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
    font-semibold tracking-wide transition duration-300
    pb-1 border-b-2 border-transparent hover:border-[#336734] hocus:text-[#336734]
  `};
`;

const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-[#336734] text-gray-100
  hocus:bg-[#336734] hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;

const LogoLink = styled(NavLink)`
  // Removed absolute positioning for desktop, it will be placed flex-item
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};
  img {
    width: 130px;
    height: auto;
    ${tw`mr-3`}
  }
`;

const MobileNavLinksContainer = tw.nav`
  flex flex-1 items-center justify-between lg:hidden relative
`;

const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
  absolute right-0 top-1/2 -translate-y-1/2
`;

const MobileNavLinks = motion(styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: min(80vw, 300px);
  height: 100vh;
  padding: 1.5rem;
  background-color: white;
  color: #1f2937;
  box-shadow: -5px 0 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`);

const MobileNavLinksList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  margin-top: 50px;
`;

const MobileNavLink = styled(NavLink)`
  ${tw`block w-full text-center px-4 py-3 rounded-md hover:bg-gray-100`};
`;

const MobilePrimaryLink = styled(PrimaryLink)`
  ${tw`block w-full text-center px-4 py-3`};
`;

const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center relative
`;

const DropdownMenu = styled.div`
  ${tw`absolute mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5`};
  z-index: 50;
  right: 0;
  margin-right: 16px;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const LogoutButton = styled.button`
  ${tw`px-4 py-2 text-gray-800 transition-colors duration-300 font-medium`};
  width: 100%;
  text-align: center;
  &:hover {
    background-color: #336734;
    color: white;
  }
`;

const NavLinkStyled = styled(Link)`
  ${tw`block px-4 py-2 text-gray-800 transition-colors duration-300 font-medium`};
  width: 100%;
  text-align: center;
  &:hover {
    background-color: #336734;
    color: white;
  }
`;

const UserButton = styled.button`
  ${tw`relative px-4 py-2 rounded-full bg-[#336734] text-white font-semibold mr-4 transition duration-300`};
  &:hover {
    background-color: #336734;
    color: #ffffff;
  }
  &:focus {
    ${tw`outline-none`};
  }
`;

const MobileUserButton = styled.div`
  ${tw`w-full text-center px-4 py-3 rounded-md`};
  background-color: #336734;
  color: white;
  font-weight: bold;
  pointer-events: none;
  margin-bottom: 1rem;
`;

export default ({
  roundedHeaderButton = false,
  logoLink,
  links, // This prop will now be ignored as we define links internally
  className,
  collapseBreakpointClass = "lg",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    localStorage.removeItem("id");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("role");
    setUser(null);
    navigate("/");
    setDropdownOpen(false);
    if (showNavLinks) {
      toggleNavbar();
    }
  };

  const handleNavLinkClick = () => {
    setDropdownOpen(false);
    toggleNavbar();
  };

  const defaultLogoLink = (
    <LogoLink to="/">
      <img src={logo} alt="logo" width={130} height={130} /> {/* Set explicit width/height here */}
    </LogoLink>
  );

  // Define desktop navigation links separately
  const desktopNavLeftLinks = (
    <NavLinks>
 <NavLink to="/produits-admin">{t('Produits')}</NavLink>
      <NavLink to="/order-admin">{t('Commandes')}</NavLink>
      <NavLink to="/retraite">{t('Retraite')}</NavLink>
      <NavLink to="/payment-admin">{t('Paiements')}</NavLink>
    </NavLinks>
  );

  const desktopNavRightLinks = (
 <NavLinks>
      <NavLink to="/user-admin">{t('Utilisateurs')}</NavLink>
      <NavLink to="/client-admin">{t('Clients')}</NavLink>
      <NavLink to="/admin-announcement">{t('annonce')}</NavLink>
      {user ? (
        <div tw="relative inline-block ml-4">
          <UserButton onClick={() => setDropdownOpen(!dropdownOpen)}>
            {user.name}
          </UserButton>
          {dropdownOpen && (
            <DropdownMenu>
              <NavLinkStyled to="/dashbord-admin" onClick={handleNavLinkClick}>
                {t('Tableau_de_bord')}
              </NavLinkStyled>
              <LogoutButton onClick={() => { handleLogout(); toggleNavbar(); }}>
                {t('Déconnexion')}
              </LogoutButton>
            </DropdownMenu>
          )}
        </div>
      ) : null}
    </NavLinks>  );

  // Mobile navigation links will use a single list for simplicity, reflecting previous structure
  const mobileNavItems = (
 <MobileNavLinksList>
      {user && <MobileUserButton>{user.name}</MobileUserButton>}
      <MobileNavLink to="/produits-admin" onClick={handleNavLinkClick}>{t('Produits')}</MobileNavLink>
      <MobileNavLink to="/order-admin" onClick={handleNavLinkClick}>{t('Commandes')}</MobileNavLink>
      <MobileNavLink to="/retraite" onClick={handleNavLinkClick}>{t('Retraite')}</MobileNavLink>
      <MobileNavLink to="/payment-admin" onClick={handleNavLinkClick}>{t('Paiements')}</MobileNavLink>
      <MobileNavLink to="/user-admin" onClick={handleNavLinkClick}>{t('Utilisateurs')}</MobileNavLink>
      <MobileNavLink to="/client-admin" onClick={handleNavLinkClick}>{t('Clients')}</MobileNavLink>
      <MobileNavLink to="/admin-announcement" onClick={handleNavLinkClick}>{t('annonce')}</MobileNavLink>
      {user ? (
        <>
          <MobileNavLink to="/dashbord-admin" onClick={handleNavLinkClick}>{t('Tableau_de_bord')}</MobileNavLink>
          <LogoutButton onClick={() => { handleLogout(); toggleNavbar(); }}>
            {t('Déconnexion')}
          </LogoutButton>
        </>
      ) : null}
    </MobileNavLinksList>  );

  return (
    <Header className={className || "header-light"}>
      {/* Desktop Navigation */}
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        {desktopNavLeftLinks} {/* Left group of links */}
        {defaultLogoLink} {/* Centered logo */}
        {desktopNavRightLinks} {/* Right group of links and user button */}
      </DesktopNavLinks>

      {/* Mobile Navigation */}
      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {/* Logo remains centered for mobile within its context */}
        <LogoLink to="/">
          <img src={logo} alt="logo" width={130} height={130} />
        </LogoLink>

        <div className="relative z-20">
          <NavToggle onClick={toggleNavbar}>
            {showNavLinks ? <CloseIcon tw="w-6 h-6 text-gray-800" /> : <MenuIcon tw="w-6 h-6 text-gray-800" />}
          </NavToggle>
        </div>

        <AnimatePresence>
          {showNavLinks && (
            <MobileNavLinks
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.2 }}
            >
              <NavToggle onClick={toggleNavbar} style={{ position: 'absolute', top: '20px', right: '20px', transform: 'none' }}>
                <CloseIcon tw="w-6 h-6 text-gray-800" />
              </NavToggle>
              {mobileNavItems}
            </MobileNavLinks>
          )}
        </AnimatePresence>
      </MobileNavLinksContainer>
    </Header>
  );
};

// Breakpoint mappings
const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};